import { useEffect, type CSSProperties, type ReactNode } from "react";

/**
 * Mobile bottom sheet — slides up from the bottom edge with a backdrop.
 *
 * Locks body scroll while open. Tap on backdrop or × button closes it.
 * Content area scrolls internally so the sheet itself never grows past its
 * max height.
 */
export function MobileSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  height = "88vh",
  bodyStyle,
  showHandle = true,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  height?: string;
  bodyStyle?: CSSProperties;
  showHandle?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.62)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          zIndex: 80,
          animation: "hh-backdrop-in 200ms ease forwards",
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          maxHeight: "94vh",
          height,
          background: "var(--bg-1)",
          borderTop: "1px solid var(--line-3)",
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          boxShadow:
            "0 -16px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
          zIndex: 90,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          animation:
            "hh-sheet-up 280ms cubic-bezier(0.32, 0.72, 0, 1) forwards",
        }}
      >
        {showHandle && (
          <div
            aria-hidden
            style={{
              padding: "8px 0 4px",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 38,
                height: 4,
                borderRadius: 4,
                background: "var(--line-3)",
              }}
            />
          </div>
        )}
        {(title || subtitle) && (
          <div
            style={{
              padding: "8px 16px 12px",
              borderBottom: "1px solid var(--line-2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexShrink: 0,
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              {subtitle && (
                <div
                  style={{
                    fontFamily: "var(--f-mono)",
                    fontSize: 9,
                    letterSpacing: "0.18em",
                    color: "var(--fg-dim)",
                    textTransform: "uppercase",
                    fontWeight: 500,
                  }}
                >
                  {subtitle}
                </div>
              )}
              {title && (
                <div
                  style={{
                    fontFamily: "var(--f-mono)",
                    fontSize: 12,
                    letterSpacing: "0.18em",
                    color: "var(--fg-max)",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginTop: subtitle ? 3 : 0,
                  }}
                >
                  {title}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                width: 28,
                height: 28,
                borderRadius: 4,
                background: "transparent",
                border: "1px solid var(--line-3)",
                color: "var(--fg-meta)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                padding: 0,
                fontSize: 14,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
        )}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            ...bodyStyle,
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
